import { validateAssessmentAnswers } from './assessmentSchema.mjs';

// Pure report scoring. Only published, trusted assessment definitions should be
// accepted by the delivery backend. Browser grades are advisory, not authority.
export function validateReportDefinition(assessment) {
  const errors=[];
  const report=assessment?.report;
  if(assessment?.reportType==='timesheet') return errors; // Legacy, unchanged.
  if(!report || report.type!=='standard' || report.version!==1) return ['Versioned standard report configuration required'];
  const questions=new Map((assessment.questions||[]).map(q=>[q.id,q]));
  if(!Array.isArray(report.dimensions) || !report.dimensions.length || report.dimensions.length>12) errors.push('Provide between 1 and 12 scored dimensions');
  const used=new Set();
  for(const dimension of report.dimensions||[]) {
    const q=questions.get(dimension.questionId);
    if(!q || q.type!=='choice') {errors.push('Dimension must reference an existing choice question');continue;}
    if(used.has(dimension.questionId)) errors.push('Question may only be scored once');
    used.add(dimension.questionId);
    if(!dimension.label || typeof dimension.label!=='string') errors.push('Dimension label required');
    if(!Number.isInteger(dimension.maxPoints) || dimension.maxPoints<1 || dimension.maxPoints>100) errors.push('Dimension maxPoints must be 1 to 100');
    for(const option of q.options) {
      const rule=dimension.options?.[option];
      if(!rule || !Number.isFinite(rule.points) || rule.points<0 || rule.points>dimension.maxPoints ||
        typeof rule.finding!=='string' || !rule.finding.trim() || typeof rule.recommendation!=='string' || !rule.recommendation.trim())
        errors.push('Missing or invalid scoring and guidance for '+dimension.questionId+' option');
    }
  }
  const grades=report.qualification?.grades;
  if(!Array.isArray(grades) || grades.length!==2 || !grades.every(v=>Number.isFinite(v) && v>=0 && v<=100) || grades[0]<=grades[1])
    errors.push('Qualification grades must be two descending percentage thresholds');
  if(!Array.isArray(report.nextSteps) || report.nextSteps.length!==3 || report.nextSteps.some(s=>typeof s!=='string'||!s.trim()))
    errors.push('Provide three lead-grade next steps');
  return errors;
}

export function buildStandardReport(assessment,answers) {
  const errors=validateReportDefinition(assessment);
  if(errors.length) throw new Error('Invalid report configuration: '+errors.join('; '));
  if(!validateAssessmentAnswers(answers,assessment.questions)) throw new Error('Invalid assessment answers');
  const dimensions=assessment.report.dimensions.map(d=>{
    const option=answers[d.questionId],rule=d.options[option];
    return {key:d.questionId,label:d.label,answer:option,score:rule.points,maxPoints:d.maxPoints,
      finding:rule.finding,recommendation:rule.recommendation};
  });
  const earned=dimensions.reduce((sum,d)=>sum+d.score,0);
  const possible=dimensions.reduce((sum,d)=>sum+d.maxPoints,0);
  const score=Math.round(earned*100/possible);
  const grade=score<=assessment.report.qualification.grades[1]?'A':
    score<=assessment.report.qualification.grades[0]?'B':'C';
  const band=score<=40?'At risk':score<=70?'Strained':'Healthy';
  const risks=[...dimensions].sort((a,b)=>a.score/a.maxPoints-b.score/b.maxPoints || a.key.localeCompare(b.key)).slice(0,3);
  return {assessmentId:assessment.id,reportVersion:assessment.report.version,title:assessment.title,
    score,grade,band,dimensions,risks,
    nextStep:assessment.report.nextSteps[grade==='A'?0:grade==='B'?1:2]};
}